<template>
	<div class="chat-log h-100 d-flex flex-column overflow-hidden">
		<div class="d-flex flex-fill justify-content-center align-items-center" v-if="loading > 0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<div v-show="loading <= 0" ref="alertLog" class="flex-fill overflow-auto dashboardChatLog" @scroll="onScroll($event)"></div>
		<div class="chatLogScrollToBottom" v-show="historyMode">
			<b-button @click="bottomOut()"><b-icon icon="arrow-down"></b-icon></b-button>
		</div>
		<div class="border-top p-2 d-flex align-items-center">
			<b-button size="sm" variant="secondary" class="mr-2" @click="togglePause()">
				<b-icon :icon="paused ? 'play-fill' : 'pause-fill'"></b-icon>
				{{paused ? 'Resume' : 'Pause'}}
			</b-button>
			<b-button size="sm" variant="secondary" @click="skip()">
				<b-icon icon="skip-end-fill"></b-icon>
				Skip
			</b-button>
		</div>
		<message-details-overlay
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
			@replay="replayAlert($event)"
		/>
	</div>
</template>

<script>
import util from '@/util';
import giftColorThresholds from '@/GiftThresholds';
import MessageDetailsOverlay from '@/components/MessageDetailsOverlay.vue';

export default {
	components: { MessageDetailsOverlay },
	props: {
		serviceIcons: Object,
	},
	data() { return {
		loading: 0,
		paused: false,
		historyMode: false,
		user: null,
		giftTypes: {},
		selectedMessage: null,
	}},
	created() {
		this.scrollPos = 0;
		this.prevHeight = 0;
		this.prevScrollHeight = 0;
	},
	async mounted() {
		try {
			this.loading++;
			this.startScrollAnimation();
			this.user = await this.$api.get('userData');
			const [alerts, giftTypesResult] = await Promise.all([
				this.$api.get('alerts'),
				this.$api.get('userGiftTypes'),
			]);
			this.loadGiftTypes(giftTypesResult.results);
			for(const alert of alerts)
				this.appendAlert(alert.message, alert.status);
			if(this.$refs.alertLog)
				this.$refs.alertLog.scrollTop = this.$refs.alertLog.scrollHeight;
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				userWidgetAlert: msg => {
					if(msg.sent_at) return;
					if(msg.type == 'tower') return;
					if(!this.historyMode) this.appendAlert(msg.message, 'queued', msg.id);
				},
				userWidgetAlertStatus: msg => {
					console.log('userWidgetAlertStatus', msg);
					const found = document.querySelector(`[data-alert-id="${CSS.escape(msg.alertId)}"]`);
					console.log('found', found);
					found.classList.forEach(cls => cls.startsWith('alert-status-') && found.classList.remove(cls));
					found.classList.add(`alert-status-${msg.status}`);
				},
			});
		} catch(e) {
			console.error(e);
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.stopScrollAnimation();
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		loadGiftTypes(giftTypes) {
			this.giftTypes = {};
			for(const giftType of giftTypes) {
				if(!(giftType.service_id in this.giftTypes))
					this.giftTypes[giftType.service_id] = {};
				this.giftTypes[giftType.service_id][giftType.gift_type] = giftType;
			}
		},
		appendAlert(msg, status, id) {
			if(!this.$refs.alertLog) return;
			const elem = util.createChatLogMessage(msg, {
				serviceIcons: this.serviceIcons,
				giftColorThresholds,
				user: this.user,
				giftTypes: this.giftTypes,
			}, {
				onclick: prepared => { this.selectedMessage = prepared; },
				onmenuclick: prepared => this.highlightMessage(prepared),
			});
			if(status) elem.classList.add(`alert-status-${status}`);
			if(id) elem.dataset.alertId = id;
			this.$refs.alertLog.appendChild(elem);
		},
		startScrollAnimation() {
			if(this._animationId) return;
			const frame = () => {
				this._animationId = window.requestAnimationFrame(frame);
				if(!this.$refs.alertLog || this.historyMode) return;
				const el = this.$refs.alertLog;
				const diff = el.scrollHeight - el.offsetHeight - el.scrollTop;
				if(diff > 400) {
					el.scrollTop += diff;
				} else {
					el.scrollTop += Math.round(diff / 5) + 1;
				}
			};
			this._animationId = window.requestAnimationFrame(frame);
		},
		stopScrollAnimation() {
			if(this._animationId) {
				window.cancelAnimationFrame(this._animationId);
				delete this._animationId;
			}
		},
		onScroll(event) {
			const el = event.target;
			const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			if(this.historyMode) {
				if(scrollBottom < 1) this.historyMode = false;
			} else {
				const scrolledPx = el.scrollTop - this.scrollPos;
				const resizedPx = el.clientHeight - this.prevHeight;
				const innerPx = el.scrollHeight - this.prevScrollHeight;
				if(innerPx === 0 && resizedPx === 0 && scrolledPx < 0)
					this.historyMode = true;
			}
			this.scrollPos = el.scrollTop;
			this.prevHeight = el.clientHeight;
			this.prevScrollHeight = el.scrollHeight;
		},
		bottomOut() {
			this.historyMode = false;
			if(this.$refs.alertLog)
				this.$refs.alertLog.scrollTop = this.$refs.alertLog.scrollHeight;
		},
		highlightMessage(msg) {
			const target = document.getElementById(msg.id);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user?.highlighted_message?.serviceId) && msg.token == this.user?.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				const highlighted = this.$refs.alertLog?.getElementsByClassName('highlighted');
				if(highlighted) for(const h of highlighted) h.classList.remove('highlighted');
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: ' + (err.error || err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},
		replayAlert(msg) {
			this.$api.post('alerts/replay', { serviceId: msg.serviceId, token: msg.token });
		},
		async togglePause() {
			try {
				if(this.paused) {
					await this.$api.post('alerts/queue/resume');
					this.paused = false;
				} else {
					await this.$api.post('alerts/queue/pause');
					this.paused = true;
				}
			} catch(e) {
				console.error(e);
			}
		},
		async skip() {
			try {
				await this.$api.post('alerts/skip');
			} catch(e) {
				console.error(e);
			}
		},
	},
};
</script>

<style lang="scss">
@import '../plugins/variable.scss';

.chat-log {
	> .dashboardChatLog {
		word-break: break-word;

		> .message {
			cursor: pointer;
			padding: 2px 4px;
			position: relative;
			img { max-width: 100%; }
			&:hover { background: rgba(255, 255, 255, .1); }
			&.highlighted {
				background: $vaporwave-white;
				color: $dark;
			}
			&.gift, &.product, &.subscription {
				background: $gold;
				color: $gold-text;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover { background: darken($gold, 5%); }
			}
			> .avatar, > .service {
				width: 24px;
				height: 24px;
				border-radius: 12px;
				object-fit: cover;
				overflow: hidden;
			}
			> .name:after { content: ' ' }
			&.chat > .name:after { content: ': ' }
			&.chat.gift > .name:after { content: ' ' }
			&.chat.product > .name:after { content: ' ' }
			> .content {
				display: inline;
				> img {
					max-width: 80px;
					max-height: 80px;
					&.emote { max-width: 32px; max-height: 32px; }
				}
			}
			&.self { color: fade-out($vaporwave-white, .4); }
			&.owner { color: $vaporwave-green; }
			> div.menubtn {
				display: none;
				position: absolute;
				font-size: 15px;
				right: 0; top: 0; bottom: 0;
				background: transparent;
				line-height: 100%;
				align-items: center;
				padding: 4px 24px;
				text-align: center;
				color: white;
				&:after { content: '⊕ Show'; }
				backdrop-filter: blur(3px);
			}
			&.highlighted > div.menubtn {
				color: $dark;
				&:after { content: '⊖ Hide'; }
			}
			&:hover > div.menubtn { display: inline-flex; }
			&:hover > div.menubtn:hover { background: rgba(255, 255, 255, .1); }
			&.alert-status-played { opacity: 0.4; }
			&.alert-status-error {
				border-left: 3px solid #e74c3c;
				background: repeating-linear-gradient(
					135deg,
					transparent,
					transparent 6px,
					rgba(231, 76, 60, 0.08) 6px,
					rgba(231, 76, 60, 0.08) 12px
				) !important;
				&:hover { background: rgba(231, 76, 60, 0.18) !important; }
			}
		}
	}

	> .chatLogScrollToBottom {
		position: absolute;
		bottom: 3.7em;
		right: 0;
		left: 0;
		text-align: center;
		pointer-events: none;
		> button { pointer-events: auto; }
	}
}
</style>
